import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Tooltip } from '@mui/material';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';

import { fetchUsageData } from '../../utils/UsageAPI';
import { useRegisterRefreshCallback } from '../../contexts/UsageRefreshContext';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import './SidebarUsageDisplay.css';

const REFRESH_INTERVALS = {
  ACTIVE: 30000,
};

const THRESHOLDS = {
  WARNING: 70,
  CRITICAL: 90,
};

const formatNumber = num => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const getUsageLevel = (used, total) => {
  if (total === 0) return 'ok';
  const percentage = (used / total) * 100;
  if (percentage >= THRESHOLDS.CRITICAL) return 'critical';
  if (percentage >= THRESHOLDS.WARNING) return 'warning';
  return 'ok';
};

const getProgressValue = (used, total) => {
  if (total === 0) return 0;
  return Math.min((used / total) * 100, 100);
};

const SidebarUsageDisplay = ({ isOpen }) => {
  const navigate = useNavigate();
  const creditBalance = useProjectStore(state => state.creditBalance);
  const billingInitialized = useProjectStore(state => state.billingInitialized);
  const [usageData, setUsageData] = useState({
    credits: { used: 0, total: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const isMountedRef = useRef(true);

  const handleContainerClick = useCallback(() => {
    navigate('/usage');
  }, [navigate]);

  const fetchInterimBillingData = useCallback(async () => {
    try {
      const usageResponse = await fetchUsageData();
      const remainingCredits =
        creditBalance != null
          ? creditBalance
          : useProjectStore.getState().creditBalance ?? 0;

      if (usageResponse.success && usageResponse.data) {
        const { usedCredits, totalCredits: reportedTotalCredits } = usageResponse.data;
        const computedTotalCredits =
          remainingCredits + (usedCredits || 0) > 0
            ? (usedCredits || 0) + remainingCredits
            : remainingCredits || reportedTotalCredits || 0;

        setUsageData({
          credits: {
            used: usedCredits || 0,
            total: computedTotalCredits || 0,
          },
        });
      } else if (remainingCredits != null) {
        setUsageData({
          credits: { used: 0, total: remainingCredits },
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching billing usage data:', error);
      setLoading(false);
    }
  }, [creditBalance]);

  const refreshUsageData = useCallback(async () => {
    if (!isMountedRef.current || !billingInitialized) return;

    try {
      await fetchInterimBillingData();

      if (isMountedRef.current) {
        setLastUpdated(new Date());
        setHasError(false);
      }
    } catch (error) {
      console.error('Error refreshing usage data:', error);
      if (isMountedRef.current) {
        setHasError(true);
      }
    }
  }, [billingInitialized, fetchInterimBillingData]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!billingInitialized) {
      setLoading(true);
      return () => {
        isMountedRef.current = false;
      };
    }

    const initializeUsageData = async () => {
      try {
        await fetchInterimBillingData();

        if (isMountedRef.current) {
          setLastUpdated(new Date());
        }
      } catch (error) {
        console.error('Error initializing usage data:', error);
        if (isMountedRef.current) {
          setHasError(true);
          setLoading(false);
        }
      }
    };

    initializeUsageData();

    return () => {
      isMountedRef.current = false;
    };
  }, [billingInitialized, fetchInterimBillingData]);

  useEffect(() => {
    if (!billingInitialized) return undefined;

    const interval = setInterval(() => {
      if (isMountedRef.current) {
        refreshUsageData();
      }
    }, REFRESH_INTERVALS.ACTIVE);

    return () => clearInterval(interval);
  }, [billingInitialized, refreshUsageData]);

  useRegisterRefreshCallback(refreshUsageData);

  const used = usageData.credits.used;
  const total = usageData.credits.total;
  const remaining = Math.max(total - used, 0);
  const usageLevel = getUsageLevel(used, total);
  const progressValue = getProgressValue(used, total);

  const meterClassName = [
    'sidebar-usage__meter',
    usageLevel === 'warning' ? 'sidebar-usage__meter--warning' : '',
    usageLevel === 'critical' ? 'sidebar-usage__meter--critical' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (loading) {
    return (
      <div className={`sidebar-usage${isOpen ? '' : ' sidebar-usage--collapsed'}`}>
        <div className="sidebar-usage__row">
          <div className="sidebar-usage__skeleton-meter" />
          {isOpen && (
            <div className="sidebar-usage__copy">
              <div className="sidebar-usage__skeleton-line sidebar-usage__skeleton-line--title" />
              <div className="sidebar-usage__skeleton-line sidebar-usage__skeleton-line--subtitle" />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (hasError) {
    return null;
  }

  const content = (
    <div
      className={`sidebar-usage${isOpen ? '' : ' sidebar-usage--collapsed'}`}
      onClick={handleContainerClick}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleContainerClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Credits remaining: ${formatNumber(remaining)}. Open usage.`}
    >
      <div className="sidebar-usage__row">
        <div className={meterClassName}>
          <CircularProgress
            className="sidebar-usage__meter-track"
            variant="determinate"
            value={100}
            size={32}
            thickness={4}
          />
          <CircularProgress
            className="sidebar-usage__meter-fill"
            variant="determinate"
            value={progressValue}
            size={32}
            thickness={4}
          />
          <span className="sidebar-usage__meter-icon">
            <MonetizationOnOutlinedIcon />
          </span>
        </div>
        {isOpen && (
          <div className="sidebar-usage__copy">
            <span className="sidebar-usage__title">Credits</span>
            <span className="sidebar-usage__subtitle">
              {formatNumber(remaining)} remaining
            </span>
          </div>
        )}
      </div>

      {isOpen && (
        <span className="sidebar-usage__meta">
          {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : 'Loading...'}
        </span>
      )}
    </div>
  );

  if (isOpen) {
    return content;
  }

  return (
    <Tooltip
      title={`Credits: ${formatNumber(used)}/${formatNumber(total)}`}
      placement="right"
    >
      {content}
    </Tooltip>
  );
};

SidebarUsageDisplay.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

export default SidebarUsageDisplay;
